import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints, getIDFromUrl, Url, withSideload } from '@rossum/api-client';
import { AnnotationsSearchQuery } from '@rossum/api-client/annotations';
import { Relation } from '@rossum/api-client/relations';
import { api } from '../../../lib/apiClient';
import { asScalar } from '../../../lib/url';
import {
  useNextCursorStore,
  usePagination,
  useRowCount,
} from '../../document-list-base/hooks/usePagination';
import {
  TransformedAnnotation,
  useTransformAnnotations,
} from '../../document-list-columns/hooks/useTransformAnnotation';
import { DISALLOWED_STATUSES_FILTER_ITEM } from '../../document-list-columns/status/statuses';
import { encodeMqlQuery } from '../../mql/encodeMqlQuery';
import { ACCESSIBLE_ANNOTATIONS_FILTER_ITEM } from '../../pricing/utils';

const SEARCH_RELATION_CANDIDATES_QUERY_KEY = 'search-relation-candidates';

export const searchRelationCandidatesPagination = { page: 0, pageSize: 5 };

export type RelationCandidate = TransformedAnnotation & {
  isInSameSubtypeRelation: boolean;
};

export const markSameSubtypeMembership = (
  annotations: TransformedAnnotation[],
  relations: Relation[],
  subtypeName: string | null
): RelationCandidate[] => {
  const sameSubtypeRelations = relations.filter(
    relation => relation.type === 'custom' && relation.name === subtypeName
  );

  return annotations.map(annotation => ({
    ...annotation,
    isInSameSubtypeRelation: sameSubtypeRelations.some(
      relation =>
        relation.parent === annotation.url ||
        relation.annotations.includes(annotation.url)
    ),
  }));
};

export const useSearchRelationCandidates = ({
  searchValue,
  subtypeName,
  rootAnnotationUrl,
  excludedAnnotations,
  defaultQueueUrl,
}: {
  searchValue: string;
  subtypeName: string | null;
  rootAnnotationUrl: Url;
  excludedAnnotations: Url[];
  // Without a search value, recent documents from this queue are listed.
  defaultQueueUrl: Url | null;
}) => {
  const searchRef = useRef(searchValue);

  const {
    searchAfter,
    paginationModel,
    handlePaginationModelChange,
    setNextToMap,
    resetPagination,
    shouldForceSetNextCursor,
  } = usePagination(searchRelationCandidatesPagination);

  const isSearching = !!searchValue;

  const mqlQuery = encodeMqlQuery([
    {
      field: 'annotation',
      value: [rootAnnotationUrl, ...excludedAnnotations],
      operator: 'notIn',
    },
    ...(isSearching || !defaultQueueUrl
      ? []
      : [
          {
            field: 'queue',
            operator: 'isAnyOf',
            value: [`${getIDFromUrl(defaultQueueUrl)}`],
          },
        ]),
    ACCESSIBLE_ANNOTATIONS_FILTER_ITEM,
    DISALLOWED_STATUSES_FILTER_ITEM,
  ]);

  const query: AnnotationsSearchQuery = {
    pageSize: searchRelationCandidatesPagination.pageSize,
    searchAfter: asScalar(searchAfter),
    ...(isSearching ? {} : { ordering: '-created_at' }),
  };

  const payload = {
    query: mqlQuery,
    ...(isSearching ? { queryString: { string: searchValue } } : {}),
  };

  const { data, isFetching, isError } = useQuery({
    queryKey: [SEARCH_RELATION_CANDIDATES_QUERY_KEY, query, payload],

    queryFn: () =>
      api.request(
        withSideload(endpoints.annotations.search(query, payload), {
          documents: true,
          modifiers: true,
          relations: true,
          childRelations: true,
        })
      ),

    enabled: !!subtypeName && (isSearching || !!defaultQueueUrl),
  });

  // go back to the first page every time we make a new search request
  if (isFetching && searchValue !== searchRef.current) {
    searchRef.current = searchValue;
    resetPagination();
  }

  const { transformedAnnotations, isLoading: isTransforming } =
    useTransformAnnotations({
      annotations: data?.results,
      documents: data?.documents,
      modifiers: data?.modifiers,
      relations: data?.relations,
      childRelations: data?.childRelations,
    });

  const candidates = markSameSubtypeMembership(
    transformedAnnotations ?? [],
    [...(data?.relations ?? []), ...(data?.childRelations ?? [])],
    subtypeName
  );

  const rowCount = useRowCount(data?.pagination.total);

  useNextCursorStore({
    gridIsLoading: isFetching,
    setNextToMap,
    next: data?.pagination.next,
    shouldForceSetNextCursor,
  });

  return {
    candidates,
    isLoading: isTransforming || isFetching,
    isError,
    paginationContext: {
      rowCount,
      paginationModel,
      handlePaginationModelChange,
    },
  };
};
